import { DragStartEvent } from "@dnd-kit/core"
import { DraggableCardTaskProps } from "../interfaces/draggable_types"
import { EmployeeNameEmail } from "../interfaces/employee_type"
import { EmployeeContainerProps } from "../components/Tasks/DragNDrop/DragNDropTasks"
import { postAssignedTasks } from "../api/tasks"

type TaskDragNDropProps = {
    project_id: string
    cards: DraggableCardTaskProps[]
    setCards: (cards: DraggableCardTaskProps[]) => void
    employee_containers: EmployeeContainerProps[]
    setEmployeeContainers: (employee_containers: EmployeeContainerProps[]) => void
    setErrorMessage: (errorMessage: string | null) => void
    employee_options: EmployeeNameEmail[]
    setEmployeeOptions: (employee_options: EmployeeNameEmail[]) => void
    setActiveId: (activeId: string | null) => void
    navigate: (path: string) => void
}

/**
 * ## useTaskDragNDrop
 * 
 * This hook handles the drag and drop functionality for the tasks
 * 
 * @param cards - The task cards to be dragged and dropped
 * @param setCards - The function to set the task cards
 * @param employee_containers - The employees that are currently assigned to the tasks
 * @param setEmployeeContainers - The function to set the employees that are currently assigned to the tasks
 * @param setErrorMessage - The function to set the error message
 * @param employee_options - The employees that are available to be assigned to the tasks
 * @param setEmployeeOptions - The function to set the employees that are available to be assigned to the tasks
 * @param setActiveId - The function to set the active id (which is the id of the card that is being dragged)
 * 
 * @returns a tuple containing the functions
 */
export default function useTaskDragNDrop({cards, setCards, employee_containers, setEmployeeContainers, setErrorMessage, setActiveId, navigate, project_id}: TaskDragNDropProps) {

    // This is used to set the active id when a card is being dragged
    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string)
    }

    // Triggers when a card is dropped or let go
    // over determines if the card is being dropped into a container or not
    // active is information about the card that is being dragged
    const handleDragEnd = (event: any) => {
        const { over } = event // This is the droppable container that the card is being dragged over
        const { active } = event // This is the draggable card that is being dragged

        setActiveId(null)

        // If the card is not being dropped into a container, we want to set the parent to null
        if (over === null) {
            setCards(cards.map((card) => ( active.id === card.id ? { ...card, parent: null } : card )))
        } else {
            // If the card is being dropped into a container, we want to set the parent to the container's id
            setCards(cards.map((card) => ( active.id === card.id ? { ...card, parent: over?.id } : card )))
            // Edit the employee container to add this task to the tasks array
            setEmployeeContainers(employee_containers.map((container) => {
                if (container.employee.value === over?.id) {
                    // Find the card that is being dragged
                    const draggedCard = cards.find((card) => card.id === active.id)
                    if (draggedCard) {
                        // Add the card to the tasks array to that employee
                        return { ...container, tasks: [...container.tasks, draggedCard] }
                    }
                }
                return container
            }))
        }
    }

    // Triggers when the user clicks the "Add Employee" button
    const handleAddEmployee = () => {
        // If there is an empty container, we don't want to add another one
        if (employee_containers.filter((container) => container.employee.value === 'Select a User').length > 0) {
            setErrorMessage("There is already container with no assigned user. Please assign a user to the container before adding another one.")
            return
        }

        const new_employee = { employee: { value: 'Select a User', label: 'Select a User', name: 'Select a User', email: 'Select a User' }, tasks: [] }
        setEmployeeContainers([...employee_containers, new_employee])
        setErrorMessage(null)
    }

    // Triggers when the user clicks the "Remove Employee" button
    const handleRemoveEmployee = (id: string) => {
        setEmployeeContainers(employee_containers.filter((container: EmployeeContainerProps) => container.employee.value !== id))
        setCards(cards.map((card) => card.parent === id ? { ...card, parent: null, selected_employee: null } : card))
    }

    // Triggers when the user selects an employee from the dropdown
    const handleEmployeeSelect = (oldId: string, newEmployee: EmployeeNameEmail) => {
        // Check if the new employee is already in the employee_containers array
        if (employee_containers.some(container => container.employee.value === newEmployee.value)) {
            setErrorMessage("This employee is already in the list of employees. Please select a different employee.")
            setEmployeeContainers(employee_containers.map(
                (container) => 
                    container.employee.value === newEmployee.value ? {
                        ...container, 
                        employee: {
                            ...container.employee,
                            name: newEmployee.name, 
                            email: newEmployee.email 
                        }
                    } : container
            ))
            return
        }

        setEmployeeContainers(employee_containers.map((container: EmployeeContainerProps) =>
            container.employee.value === oldId ? {
                ...container,
                employee: newEmployee
            } : container
        ))

        setCards(cards.map((card: DraggableCardTaskProps) =>
                card.parent === oldId ? {
                    ...card,
                    selected_employee: newEmployee.label,
                    parent: newEmployee.value
                } : card
            )
        )

        setErrorMessage(null)
    }

    /*
        The idea behind this function is that it will look over all the cards that have been assigned to an employee
        and send that information to the backend to create the tasks objects automatically
    */
    const handleOnSubmit = async () => {
        // Error handling
        if (!checkForErrors()) return

        if (!project_id) {
            setErrorMessage("Please select a project before submitting.")
            return
        }

        const data = {
            project_id: project_id,
            cards: cards
        }

        try {
            const response = await postAssignedTasks(data)
            if (response.operation === "COMPLETED") {
                alert("Tasks assigned successfully")
                navigate("/tasks")
            } else {
                setErrorMessage("Error assigning tasks")
            }
        } catch (error) {
            console.error("Error posting assigned tasks: ", error)
        }
    }

    // This sets all cards' parent to null and the selected_employee to null
    const handleOnReset = () => {
        if (confirm("Are you sure you want to reset the tasks? This will remove all the tasks from the employees and reset the employee list.")) {
            setCards(cards.map((card) => ({ ...card, parent: null, selected_employee: null })))
            setEmployeeContainers([])
            setErrorMessage(null)
        }
    }

    // For onSubmit
    const checkForErrors = (): boolean => {
            // If no employees are assigned, we don't want to submit
            if (employee_containers.length === 0) {
                if (confirm("There are no employees assigned to the tasks. Are you sure you want to submit?")) {
                    alert("Submitting...")
                    return true
                }
                return false
            } 
            // If there is a container with no assigned employee, we don't want to submit
            else if (employee_containers.some((container) => /^Select a User(?: \d+)?$/i.test(container.employee.value))) {
                setErrorMessage("There is a container with no assigned employee. Please assign an employee to the container or delete the container before submitting.")
                return false
            }
            // You can submit without the essential tasks, but you need to confirm it
            else if (cards.some((card) => card.parent === null)) {
                if (confirm("There are essential tasks that have not been assigned to an employee. Are you sure you want to submit?"))
                    return true
                
                return false
            }
            // If all the cards have been assigned to an employee, we can submit
            else {
                alert("Submitting...")
                return true
        }
    }

    return { handleDragEnd, handleAddEmployee, handleRemoveEmployee, handleEmployeeSelect, handleDragStart, handleOnSubmit, handleOnReset }
}